import React, {useCallback, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Store} from "pullstate";
import {toast} from "react-toastify";
import {postLogin} from "./api/login";
import moment from "moment/moment";
import {ModalStore} from "./components/NewBooking";
import {userBookingsSelector} from "./components/selectors/userBookingsSelector";
import {deleteBooking} from "./api/booking";
import {defineBookedCoursesSelector} from "./components/selectors/defineBookedCoursesSelector";
import {sortedCoursesSelector} from "./components/selectors/sortedCoursesSelector";
import {stringToDateConvertor} from "./components/selectors/stringToDateConvertor";
import {syncWithServer} from "./components/actions/syncWithServer";

export const AppStore = new Store({
    golfCourses: [],
    unavailableCourses: null,
    userInAdvanceBookings: null,
    userTodayBookings: null,
    menuOpen: false,
    isDev: false,
    bookings: [],
    userBookings: [],
    selectedDate: moment().format("YYYY-MM-DD"),
    selected: null,
    authUser: null,
    options: false,
    loading: false,
    loadingUser: false,
    OTP: null,
    verifiedOTP: false,
    showPassword: true,
    showConfirmPassword: false,
    confirmPassword: "",
    showOTP: false,
    emailError: false,
    showMembership: false,
    showHistory: false,
    modalIsOpen: false,
    reloadData: true,
    email: "",
    password: "",
    user: null,
    loginPage: false,
    forgotPassword: true
});

AppStore.subscribe(s => s.selected, (selected) => {
    const modalIsOpen = !!selected;
    ModalStore.update(s => {
        s.showMemberModal = modalIsOpen;
    });
});

export const useUser = () => {
    const user = AppStore.useState(s => s.user);
    const setUser = (value) => AppStore.update(s => {
        s.user = value;
    });
    return [user, setUser];
}

export const setMenuOpen = (value) => AppStore.update(s => {
    s.menuOpen = value;
});

export const useMenuOpen = () => {
    const menuOpen = AppStore.useState(s => s.menuOpen);

    return [menuOpen, setMenuOpen];
}


export const useDateValue = () => {
    const dateValue = AppStore.useState(s => s.selectedDate);
    const setDateValue = (value) => AppStore.update(s => {
        s.selectedDate = value;
    });
    return [dateValue, setDateValue];
}

export const setShowHistory = (value) => AppStore.update(s => {
    s.showHistory = value;
    s.menuOpen = false;
});

export const setLoginPage = (value) => {
    AppStore.update(s => {
        s.loginPage = value;
        s.openMenu = false;
    })
    if (!value) {
        syncWithServer();
    }
};

export const useHistory = () => {
    const showHistory = AppStore.useState(s => s.showHistory);
    return [showHistory, setShowHistory];
}

export const useDeleteBooking = () => {
    const bookings = AppStore.useState(s => s.bookings);

    return useCallback((id) => {
        setLoading(true)
        deleteBooking({id}).then(() => {
            setBookings(bookings.filter(item => item.id !== id));
            setLoading(false)
        })

    }, [bookings])

}

export const getCantBookInAdvance = (date, unavailableCourses, courseId) => {
    if(unavailableCourses && date && courseId){
        if(unavailableCourses.multiYear){
            const paidYear = date.getMonth() < 3 ? date.getFullYear() - 1 : date.getFullYear();
            return !!unavailableCourses[paidYear][courseId];
        }
        return !!unavailableCourses[courseId]
    }
    return false;
}

export const useSelectDay = () => {
    const {user, selectedDate, unavailableCourses,} = AppStore.useState(s => ({
        user: s.user,
        selectedDate: s.selectedDate,
        unavailableCourses: s.unavailableCourses,
    }));

    return useCallback((item, index, dayItem) => {

        if (!user) {
            toast.error(<a onClick={() => setLoginPage(true)} href={'#login'}>Voit tehdä ennakkovarauksia vain, kun olet kirjautunut sisään</a>);
            return;
        }

        // console.log('[[select day]]', {item, index, dayItem});
        const date = stringToDateConvertor(selectedDate);
        //day is today
        const isSelectedDayToday = date.toDateString() === new Date().toDateString();
        const isSelectDayToday = isSelectedDayToday && index === 0;

        const cantBookToday = !!(isSelectDayToday && item.data.isTodayBooked);
        // in unavailableCourses is coen
        // const cantBookAdvance = unavailableCourses ? unavailableCourses[Number(item.id)] : false;
        const cantBookAdvance = getCantBookInAdvance(date, unavailableCourses, Number(item.id));

        const isLocationAdmin = user?.roleId === 2;
        if (!isLocationAdmin && (isSelectDayToday ? cantBookToday : cantBookAdvance)) {
            toast.error('Ennakkovarausoikeus käytetty');
            return;
        }


        if (dayItem.disabled) {
            toast.error('Tämä päivä ei ole varattavissa');
            return;
        }

        if (dayItem.slotLeft === 0) {
            toast.error('Kaikki ajat varattu');
            return;
        }
        AppStore.update((s) => {
            s.selected = {
                date: moment(selectedDate).add(index, "days").format("DD/MM/YYYY"),
                item,
                index: index,
                auth: user,
            }
        });
    }, [user, selectedDate])
}

export const clearSelectedDay = () => {
    AppStore.update((s) => {
        s.selected = null;
    });
}

export const setBookings = (bookings) => {
    AppStore.update((s) => {

        s.bookings = bookings;
        const userBookings = bookings ? userBookingsSelector(bookings, s.user) : [];
        if (userBookings.length > 0) {
            s.golfCourses = defineBookedCoursesSelector(s.golfCourses, userBookings, s.isDev);
        }
        s.userBookings = userBookings;
    })
};


export const useCourses = () => {
    const {loading, bookings, userBookings, golfCoursesList} = AppStore.useState(s => ({
        loading: s.loading,
        bookings: s.bookings,
        userBookings: s.userBookings,
        golfCoursesList: s.golfCourses,
    }));
    // console.log('[[useCourses]]', loading, {golfCoursesList, bookings})
    const isLoading = loading;
    return isLoading ? [] : golfCoursesList;
}


export const setLoading = (isLoading) => AppStore.update((s) => {
    s.loading = isLoading
});

export const setLogin = (isFirst, red_email, red_password, callBack) => {
    let postEmail = isFirst ? red_email : email;
    let postPassword = isFirst ? red_password : password;
    // console.log({ email, password });
    postLogin({userName: postEmail, password: postPassword, isFirst}, data => {
        setUser(data?.data?.response?.data);
        AppStore.update((s) => {
            s.loginPage = false;
            s.forgotPassword = false;
            s.loading = false;
        });
    });
};

export const setUser = (user) => {
    if (user) {
        localStorage.setItem("user", JSON.stringify(user));
        if (user.roleId !== 2) toast.success(`Tervetuloa ${user.firstName} ${user?.LastName}`);
        if (user.roleId === 2) toast.success(`Tervetuloa ${user?.userName}`);
        AppStore.update((s) => {
            if (user.roleId !== 2) {
                s.showMembership = true;
            }
            s.user = user
            s.golfCourses = sortedCoursesSelector(user, s.golfCourses);
        })
    } else {
        localStorage.removeItem("user");
        AppStore.update((s) => {
            s.user = null;
            s.openMenu = false;
        })
    }
}

export const setShowMembership = (show) => {
    AppStore.update((s) => {
        s.showMembership = show;
        s.openMenu = false;
    });
}

export const setGolfCourses = (courses) => AppStore.update((s) => {
    if (!s.isDev) {
        courses = courses.filter(i => i.id !== "15c4f96d-8bfd-4907-9e43-5c7139fdbbf2");
    }
    s.golfCourses = sortedCoursesSelector(s.user, courses);
});


export function useBackgroundAuth() {
    useEffect(() => {
        const user = localStorage.getItem("user");
        if (!!user && user !== "null" && user !== "undefined") {
            try {
                const userObj = JSON.parse(user);
                setUser(userObj);
            } catch (e) {
                console.error(e.message)
                toast.error("Something went wrong")
            }

        }
    }, [])
}

export const setShowOTP = (show) => AppStore.update((s) => {
    s.showOTP = show
});


export function useAppState() {
    const state = AppStore.useState();
    const dispatch = useDispatch();
    const preAuthUser = useSelector((state) => state.global.preAuthUser);
    const reloadData = useSelector((state) => state.global.reloadData);
    const loginPage = useSelector((state) => state.global.loginPage);
    const golfCoursesList = useCourses()

    const stepBag = {
        firstStep: (
            <p>
                SGS palvelu vaatii tunnistautumista 1. kerralla. <br/>
                1) Syötä sähköpostiosoite, jonka olet rekisteröinyt SGS:lle alla näkyvään kenttään ja paina kirjaudu
                painiketta. <br/>
                2) Saat varmennekoodin (saattaa mennä hetki, tarkista myös roskapostisi) ja syötä nelinumeroinen koodi
                sivulle avautuvaan
                kenttään. <br/>
                3) Määrittele haluamasi salasana. Tämän jälkeen voit kirjautua sisään joko SGS jäsenumerollasi tai
                sähköpostiosoitteella
                ja valitsemallasi salasasanalla. <br/>
                Huomioi, että jäsenmaksusi maksamisen jälkeen kestää arviolta 2 pankkipäivää, kunnes digitaalinen
                jäsenkorttisi on
                käytettävissä.
            </p>
        ),
        secondStep: "",
    };

    const resetModel = () => {
        AppStore.update((s) => {
            s.OTP = null;
            s.verifiedOTP = false;
            s.showPassword = false;
            s.showConfirmPassword = false;
            s.confirmPassword = "";
            s.showOTP = false;
        });
    };


    const loading = state.loading || state.bookings.length === 0;
    // const loading = true;


    return {
        ...state,
        loading,
        preAuthUser,
        dispatch,
        stepBag,
        reloadData,
        loginPage,
        resetModel,
        setLogin,
        golfCoursesList,
        setGolfCourses,
        setBookings,
        setAuthUser: (user) => AppStore.update((s) => {
            s.authUser = user
        }),
        setLoading,
        setLoadingUser: (isLoading) => AppStore.update((s) => {
            s.loadingUser = isLoading
        }),
        setOTP: (otp) => AppStore.update((s) => {
            s.OTP = otp
        }),
        setVerifiedOTP: (isVerified) => AppStore.update((s) => {
            s.verifiedOTP = isVerified
        }),
        setShowPassword: (show) => AppStore.update((s) => {
            s.showPassword = show
        }),
        setShowConfirmPassword: (show) => AppStore.update((s) => {
            s.showConfirmPassword = show
        }),
        setConfirmPassword: (pwd) => AppStore.update((s) => {
            s.confirmPassword = pwd
        }),
        setShowOTP,
        setEmailError: (error) => AppStore.update((s) => {
            s.emailError = error
        }),
        setIsOpen: (isOpen) => AppStore.update((s) => {
            s.modalIsOpen = isOpen
        }),
        setEmail: (newEmail) => AppStore.update((s) => {
            s.email = newEmail
        }),
        setPassword: (newPassword) => AppStore.update((s) => {
            s.password = newPassword
        }),
        setUser,
        setShowMembership,
    };
}
